import { useCallback, useSyncExternalStore } from "react";

export type ThemeMode = "light" | "dark" | "system";

function getIsDark(): boolean {
  if (typeof document === "undefined") return true;
  return document.documentElement.classList.contains("dark");
}

function getMode(): ThemeMode {
  if (typeof localStorage === "undefined") return "system";
  return (localStorage.getItem("theme") as ThemeMode) || "system";
}

function applyTheme(mode: ThemeMode) {
  const prefersDark = typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const shouldBeDark = mode === "dark" || (mode === "system" && prefersDark);
  document.documentElement.classList.toggle("dark", shouldBeDark);
}

function subscribe(callback: () => void): () => void {
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  // Also listen for system preference changes
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", callback);
  return () => {
    observer.disconnect();
    mq.removeEventListener("change", callback);
  };
}

// Custom event for same-tab mode changes
const THEME_CHANGE_EVENT = "linabase-theme-change";

export function useTheme() {
  const isDark = useSyncExternalStore(subscribe, getIsDark, () => true);
  const mode = useSyncExternalStore(
    (cb) => {
      const onStorage = (e: StorageEvent) => { if (e.key === "theme") cb(); };
      window.addEventListener("storage", onStorage);
      window.addEventListener(THEME_CHANGE_EVENT, cb);
      return () => {
        window.removeEventListener("storage", onStorage);
        window.removeEventListener(THEME_CHANGE_EVENT, cb);
      };
    },
    getMode,
    () => "system" as ThemeMode,
  );

  const setMode = useCallback((m: ThemeMode) => {
    if (m === "system") {
      localStorage.removeItem("theme");
    } else {
      localStorage.setItem("theme", m);
    }
    applyTheme(m);
    window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
  }, []);

  return { isDark, mode, setMode };
}
